import React from "react";
import PropTypes from "prop-types";
import { Card, CardBody, CardTitle, CardSubtitle, Button } from "reactstrap";
import { Link } from "react-router-dom";
import FormattedNumber from "../FormattedNumber";
import ResponsivePicture from "../ResponsivePicture";
import LazyLoad from "react-lazyload";
import { REACT_APP_SHOP_CDN_SERVER } from "../../utils/env";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import shopProductSelectors from "../../redux/shop/products/selectors";

const ShopProductCard = (props, context) => {
  const {
    titleTag,
    onClickOverwrite,
    setSelected,
    selected,
    productId,
    closeSearch,
  } = props;

  const { lang, productsTmp } = useSelector((state) => ({
    lang: state.i18nState.lang,
    productsTmp: state.shopProducts.list,
  }));

  const product = useSelector((state) =>
    shopProductSelectors.selectProductById(state, lang, productId)
  )?.data;

  const dispatch = useDispatch();
  const handleProductClick = (event) => {
    event.preventDefault();
    if (product?.external) {
      window.location.href = product?.external;
    } else {
      const slugOrId = product?.attributes
        ? product?.attributes.slug
        : product?.id;
      dispatch(push(`/${lang}/i/${slugOrId}/`));
      if (closeSearch) closeSearch();
    }
  };

  const handleProductClickContactForm = (productId) => {
    setSelected(productId);
    if (onClickOverwrite) onClickOverwrite(productId);
  };

  const SHOP_API_URL = REACT_APP_SHOP_CDN_SERVER || "https://sdn.myfolie.com";
  const hostPrefix = product?.attributes ? SHOP_API_URL : "";
  const productThumbnail = productsTmp?.included?.filter((incl) => {
    return (
      incl.type === "image" &&
      incl.id === product?.relationships?.images?.data[0]?.id
    );
  })[0]?.attributes?.styles[2];
  const imgSrcs = productThumbnail
    ? `${hostPrefix}${productThumbnail.url} ${
        productThumbnail.width + "w" || "640w"
      }`
    : "";

  const renderProductImage = (external) => {
    const component = (
      <LazyLoad>
        <ResponsivePicture
          mediumData={{
            url: imgSrcs,
            metadata: {
              width: 640,
              height: 640,
            },
          }}
          className="card-img-top"
          altTitle={
            product?.attributes ? product?.attributes.name : product?.title
          }
        />
      </LazyLoad>
    );

    if (external) {
      return <a href={product?.external}>{component}</a>;
    }

    const slugOrId = product?.attributes
      ? product?.attributes.slug
      : product?.id;
    return <Link to={`/${lang}/i/${slugOrId}/`}>{component}</Link>;
  };

  return product ? (
    <Card
      className={`shop-product-card h-100 ${
        selected === product?.id ? "selected" : ""
      }`}
      onClick={
        onClickOverwrite
          ? () => handleProductClickContactForm(product?.id)
          : handleProductClick
      }
    >
      {renderProductImage(!!product?.external)}
      <CardBody>
        <CardTitle className="h5" tag={titleTag}>
          {product?.attributes ? product?.attributes.name : product?.title}
        </CardTitle>
        <CardSubtitle>
          {!!product?.saleprice && (
            <span className="sale-price">
              {context.t("from") + " "}
              <FormattedNumber
                value={parseFloat(product?.saleprice / 100)}
                language={lang}
                unit="€"
                decimals={2}
              />
              <s>
                <FormattedNumber
                  value={parseFloat(
                    product?.attributes
                      ? product?.attributes.price
                      : product?.price
                  )}
                  language={lang}
                  unit="€"
                  decimals={2}
                />
              </s>
            </span>
          )}

          {(!product?.saleprice || product?.saleprice === 0) && (
            <>
              <span className="text-remark">{context.t("from") + " "}</span>
              <span className="text-primary text-bold">
                <FormattedNumber
                  value={parseFloat(
                    product?.attributes
                      ? product?.attributes.price
                      : product?.price
                  )}
                  language={lang}
                  unit="€"
                  decimals={2}
                />
              </span>
            </>
          )}
          {!!product?.basicprice && (
            <div className="text-remark">
              ({context.t("from")}{" "}
              <FormattedNumber
                className="basic-price"
                value={parseFloat(product?.basicprice.price / 100)}
                language={lang}
                unit={`€ / ${
                  product?.basicprice.unit ? product?.basicprice.unit : "m²"
                }`}
                decimals={2}
              />
              )
            </div>
          )}
          {!product?.basicprice && (
            <span className="ml-1 text-remark">
              {context.t("gross / piece")}
            </span>
          )}
        </CardSubtitle>
        <div className="text-right mt-1">
          <Button size="sm" color="link">
            {product?.attributes?.customizable
              ? context.t("Customize")
              : context.t("View")}
          </Button>
        </div>
      </CardBody>
    </Card>
  ) : (
    <></>
  );
};

ShopProductCard.contextTypes = {
  t: PropTypes.func,
};

ShopProductCard.propTypes = {
  titleTag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
};

export default ShopProductCard;
